import React, { useState, useContext, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { DropDown } from "../../ui/DropDown";
import { Field, Formik } from "formik";
import { useRouteMatch, Switch, Route, useHistory } from "react-router-dom";
import PerspectiveAccordion from "./PerspectiveAccordion";
import PerspectiveView from "./PerspectiveView";
import ObjectiveView from "./ObjectiveView";
import KPIView from "./KPIView";
import { GetHook } from "../../api/api";
import { SCORECARDS_URL, SCORECARD_URL } from "../../api/urls";
import { get } from "../../utils/get";
import { AuthContext } from "../../context/AuthContext";
import ScorecardView from "./ScorecardView";
import moment from "moment";
import { ScorecardSubject } from "../../subjects/ScorecardSubject";
import { DateFilterSubject } from "../../subjects/DateFilterSubject";
import { DateProvider } from "../../context/DateFilterContext";
import useAuth from "../../hooks/useAuth";

interface DateFilter {
  startDate: string;
  endDate: string;
}

export const Scorecard = () => {
  const { path } = useRouteMatch();
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    startDate: "2024-01-01",
    endDate: "2024-12-31"
  });
  const { location, push } = useHistory();

  const auth = useAuth();
  const { url } = useRouteMatch();

  const lastSegment = location.pathname.split("/").pop();

  const { isAdmin, authState } = auth;

  useEffect(() => {
    setDateFilter({
      startDate: "2024-01-01",
      endDate: "2024-12-31"
    });
    console.log(dateFilter, 'is')
  }, []);

  const date =
    get(dateFilter, "startDate") && get(dateFilter, "endDate")
      ? `interval=2024-01-01/2024-12-31`
      : "";

  // setDateFilter(date);

  // console.log('What is the date', dateFilter);

  const isAdminUser = isAdmin();
  const baseUrl = `${SCORECARDS_URL}?limit=5000`;

  const excludeStatuses = ["draft", "pending-approval"];
  if (!isAdminUser) {
    excludeStatuses.push("archived");
  }

  const excludeParams = excludeStatuses.map(
    (status) => `exclude_status=${status}`
  ).join("&");

  const endPoint = !isAdminUser
    ? `&owner_id=${get(authState, "user.id")}&show_children=True`
    : "";

  const { data } = GetHook(`${baseUrl}&${excludeParams}${endPoint}`);
  
  const [selectedScorecard, setSelectedScorecard] = useState<any>([]);
  const ownerScorecard = data
    .filter((scorecard) => scorecard.owner.id === get(authState, "user.id") && scorecard.status === "active")
    .map((sc) => {
      return {
        label: get(sc, "display_name"),
        value: get(sc, "id"),
        owner_id: get(sc, "owner.id"),
      };
    });
    // console.log(data);
   
  const scorecards = data.map((sc) => {
    return {
      label: get(sc, "display_name"),
      value: get(sc, "id"),
      owner_id: get(sc, "owner.id"),
    };
  });

  const { data: selectedScorecardData } = GetHook(
    get(selectedScorecard, "value")
      ? `${SCORECARD_URL}/${get(selectedScorecard, "value", "")}/summary`
      : "null"
  );
  
  return (
    <DateProvider>
      <Formik
        enableReinitialize
        validateOnChange
        initialValues={{
          scorecard: ownerScorecard.length ? ownerScorecard[0] : scorecards[1],
        }}
        onSubmit={() => console.log("submitted")}
      >
        {({ values }) => {
          setSelectedScorecard(values.scorecard);
          ScorecardSubject.next(values.scorecard?.owner_id);
          return (
            <Container>
              <SideNav>
                <Header>
                  <Field
                    name="scorecard"
                    options={scorecards}
                    component={StyledDropDown}
                    width="13rem"
                    isClearable={false}
                    isBordered
                  />
                </Header>
                <PerspectiveAccordion
                  dateFilter={dateFilter}
                  lastLocationP={lastSegment}
                  path={path}
                  scorecardId={get(selectedScorecard, "value", "")}
                />
              </SideNav>
              <ScorecardContainer>
                <Switch>
                  <Route
                    path={`${path}/:scorecard_id/perspective/:perspective_id/objective/:objective_id/kpi/:kpi_id/`}
                    render={() => (
                      <KPIView
                        setDateFilter={setDateFilter}
                        dateFilter={dateFilter}
                        scorecard={selectedScorecardData && selectedScorecardData}
                      />
                    )}
                  />
                  <Route
                    path={`${path}/:scorecard_id/perspective/:perspective_id/objective/:objective_id/`}
                    render={() => (
                      <ObjectiveView
                        setDateFilter={setDateFilter}
                        dateFilter={dateFilter}
                        scorecard={selectedScorecardData && selectedScorecardData}
                      />
                    )}
                  />
                  <Route
                    path={`${path}/:scorecard_id/perspective/:perspective_id/`}
                    render={() => (
                      <PerspectiveView
                        setDateFilter={setDateFilter}
                        dateFilter={dateFilter}
                        scorecard={selectedScorecardData && selectedScorecardData}
                        locationState={lastSegment}
                      />
                    )}
                  />
                  <Route
                    path={`${path}`}
                    render={() => (
                      <ScorecardView
                        path={path}
                        setDateFilter={setDateFilter}
                        dateFilter={dateFilter}
                        selectedScorecard={
                          selectedScorecardData && selectedScorecardData
                        }
                      />
                    )}
                  />
                </Switch>
              </ScorecardContainer>
            </Container>
          );
        }}
      </Formik>
    </DateProvider>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(-20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 18em auto;
  background-color: #f5f6fb;
  min-height: 100vh;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid rgba(55, 81, 255, 0.1);
  padding: 2em 1em;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  span {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const SideNav = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  animation: ${slideIn} 0.5s ease-out;
`;

const ScorecardContainer = styled.div`
  background-color: #fff;
  border-right: 1px solid rgba(55, 81, 255, 0.1);
  padding: 2em;
  animation: ${fadeIn} 0.5s ease-out;
  overflow-y: auto;
`;

const StyledDropDown = styled(DropDown)`
  .react-select__control {
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      border-color: #3751ff;
    }
  }

  .react-select__option {
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #f0f2ff;
    }
  }
`;